import React, { useEffect, useState } from 'react';
import { useAdmin } from './AdminContext';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Building2, Truck, Users, AlertTriangle, Clock, Package, Mail, Database, Plus } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const { authFetch, user, isAdmin } = useAdmin();
  const [stats, setStats] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [clearing, setClearing] = useState(false);

  const loadStats = async () => {
    try {
      const res = await authFetch('/api/admin/dashboard/stats');
      if (res.ok) setStats(await res.json());
    } catch {}
  };

  useEffect(() => { loadStats(); }, []);

  const seedData = async () => {
    setSeeding(true);
    try {
      const res = await authFetch('/api/admin/seed', { method: 'POST' });
      if (res.ok) { toast.success('Sample data created'); loadStats(); }
    } catch {} finally { setSeeding(false); }
  };

  const clearData = async () => {
    setClearing(true);
    try {
      const res = await authFetch('/api/admin/seed', { method: 'DELETE' });
      if (res.ok) { toast.success('Sample data cleared'); loadStats(); }
    } catch {} finally { setClearing(false); }
  };

  const StatCard = ({ icon: Icon, label, value, color, sub, to }) => (
    <Link to={to || '#'} className="block">
      <Card className="border border-gray-200 hover:shadow-md transition-shadow duration-150 cursor-pointer">
        <CardContent className="p-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium">{label}</p>
              <p className="text-3xl font-bold mt-1" style={{ color: color || '#1B2A6B', fontFamily: 'Chivo, sans-serif' }}>{value ?? '-'}</p>
              {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
            </div>
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color || '#1B2A6B'}15` }}>
              <Icon className="w-5 h-5" style={{ color: color || '#1B2A6B' }} />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#1B2A6B', fontFamily: 'Chivo, sans-serif' }}>Dashboard</h1>
          <p className="text-sm text-gray-500">Welcome back, {user?.name}</p>
        </div>
        {isAdmin() && (
          <div className="flex gap-2">
            <Button onClick={seedData} disabled={seeding} variant="outline" size="sm" className="gap-1 text-xs" data-testid="seed-btn">
              <Database className="w-3.5 h-3.5" /> {seeding ? 'Adding...' : 'Add Sample Data'}
            </Button>
            <Button onClick={clearData} disabled={clearing} variant="outline" size="sm" className="gap-1 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid="clear-btn">
              {clearing ? 'Clearing...' : 'Clear Samples'}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Building2} label="Active Clients" value={stats?.active_clients} color="#1B2A6B" sub={`${stats?.inactive_clients || 0} inactive`} to="/admin/clients" />
        <StatCard icon={Truck} label="Active Vendors" value={stats?.active_vendors} color="#B8962E" sub={`${stats?.inactive_vendors || 0} inactive`} to="/admin/vendors" />
        {stats?.expired_coi > 0 && (
          <StatCard icon={AlertTriangle} label="Expired COI" value={stats?.expired_coi} color="#DC2626" sub="Needs immediate attention" to="/admin/vendors" />
        )}
        {stats?.expiring_coi > 0 && (
          <StatCard icon={Clock} label="COI Expiring Soon" value={stats?.expiring_coi} color="#F59E0B" sub="Within 30 days" to="/admin/vendors" />
        )}
        {isAdmin() && <StatCard icon={Users} label="Active Users" value={stats?.total_users} color="#6366F1" to="/admin/users" />}
        <StatCard icon={Package} label="Shipment Requests" value={stats?.total_shipments} color="#0EA5E9" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border border-gray-200">
          <CardContent className="p-5">
            <h3 className="font-semibold mb-3" style={{ color: '#1B2A6B' }}>Quick Actions</h3>
            <div className="space-y-2">
              <Link to="/admin/clients?action=new"><Button variant="outline" className="w-full justify-start gap-2"><Plus className="w-4 h-4" style={{ color: '#B8962E' }} /> Add New Client</Button></Link>
              <Link to="/admin/vendors?action=new"><Button variant="outline" className="w-full justify-start gap-2"><Plus className="w-4 h-4" style={{ color: '#B8962E' }} /> Add New Vendor</Button></Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
